/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { alpha } from '@mui/material/styles';

import { getCommandIntelligenceTokens } from '../../theme/commandIntelligenceTokens';

/**
 * @param {import('@mui/material').Theme & { validateMode?: Function }} theme
 * @param {{ isThinkingPhase?: boolean }} options
 */
export const getCommandIntelligenceReasoningStyles = (theme, { isThinkingPhase = false } = {}) => {
  const tokens = getCommandIntelligenceTokens(theme);

  return {
    root: {
      mb: 1.25,
      borderRadius: tokens.radiusChip,
      border: `1px solid ${tokens.borderSubtle}`,
      bgcolor: alpha(tokens.surfaceChrome, 0.65),
      overflow: 'hidden',
    },
    trigger: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      width: '100%',
      px: 1,
      py: 0.75,
      border: 0,
      bgcolor: 'transparent',
      cursor: 'pointer',
      textAlign: 'left',
      fontFamily: tokens.fontStack,
      color: tokens.textSecondary,
      '&:hover': {
        bgcolor: alpha(tokens.accentPrimary, 0.06),
      },
    },
    triggerIcon: {
      fontSize: '1rem',
      color: tokens.accentPrimary,
      flexShrink: 0,
    },
    triggerLabel: {
      flex: 1,
      fontSize: '0.8125rem',
      fontWeight: 500,
      lineHeight: 1.3,
      ...(isThinkingPhase
        ? {
            backgroundImage: `linear-gradient(90deg, ${tokens.textMuted} 0%, ${tokens.accentPrimary} 50%, ${tokens.textMuted} 100%)`,
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'ciReasoningShimmer 1.8s ease-in-out infinite',
            '@keyframes ciReasoningShimmer': {
              '0%': { backgroundPosition: '100% 50%' },
              '100%': { backgroundPosition: '0% 50%' },
            },
          }
        : {
            color: tokens.textSecondary,
          }),
    },
    chevron: {
      fontSize: '1.1rem',
      color: tokens.textMuted,
      transition: 'transform 180ms ease',
    },
    chevronOpen: {
      transform: 'rotate(180deg)',
    },
    content: {
      px: 1.25,
      pb: 1,
      pt: 0.25,
      borderTop: `1px solid ${tokens.borderSubtle}`,
      color: tokens.textSecondary,
      fontFamily: tokens.fontStack,
      fontSize: '0.8125rem',
      lineHeight: 1.55,
      '& p': {
        my: 0,
        mb: 0.75,
        '&:last-child': { mb: 0 },
      },
    },
    waitingText: {
      fontStyle: 'italic',
      color: tokens.textMuted,
    },
  };
};
